import * as React from 'react';

import { ArrowLeft, Close } from '@mint-lib/icons';
import { cn } from '@mint-lib/tw-utils';
import { Dialog as DrawerPrimitive } from 'radix-ui';

import { typographyVariants } from './typography.jsx';
import { buttonVariants } from './variants.jsx';

function Drawer({
  ...props
}: Readonly<React.ComponentProps<typeof DrawerPrimitive.Root>>) {
  return <DrawerPrimitive.Root data-slot="drawer" {...props} />;
}

function DrawerTrigger({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Trigger>) {
  return <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />;
}

function DrawerClose({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Close>) {
  return <DrawerPrimitive.Close data-slot="drawer-close" {...props} />;
}

function DrawerPortal({
  ...props
}: Readonly<React.ComponentProps<typeof DrawerPrimitive.Portal>>) {
  return <DrawerPrimitive.Portal data-slot="drawer-portal" {...props} />;
}

function DrawerOverlay({
  className,
  ...props
}: Readonly<React.ComponentProps<typeof DrawerPrimitive.Overlay>>) {
  return (
    <DrawerPrimitive.Overlay
      data-slot="drawer-overlay"
      className={cn(
        'fixed inset-0 z-overlay bg-ui-overlay data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  );
}
function DrawerRoot({
  className,
  children,
  size = '75%',
  side = 'right',
  ...props
}: Readonly<
  React.ComponentProps<typeof DrawerPrimitive.Content> & {
    size?: '25%' | '50%' | '75%';
    side?: 'right' | 'bottom';
  }
>) {
  return (
    <DrawerPrimitive.Content
      data-slot="drawer-content"
      data-size={size}
      data-side={side}
      className={cn(
        'group/drawer fixed z-dialog flex flex-col gap-4 bg-ui-primary shadow-lg transition ease-in-out data-[side=bottom]:inset-x-0 data-[side=bottom]:inset-y-auto data-[side=bottom]:bottom-0 data-[side=bottom]:h-1/2 data-[side=bottom]:w-full data-[side=bottom]:rounded-t-3xl data-[side=bottom]:bg-background-default data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-full data-[side=right]:border-l-border-primary data-[state=closed]:animate-out data-[side=bottom]:data-[state=closed]:slide-out-to-bottom data-[side=right]:data-[state=closed]:slide-out-to-right data-[state=open]:animate-in data-[side=bottom]:data-[state=open]:slide-in-from-bottom data-[side=right]:data-[state=open]:slide-in-from-right md:data-[side=right]:data-[size=25%]:w-1/4 md:data-[side=right]:data-[size=50%]:w-1/2 md:data-[side=right]:data-[size=75%]:w-3/4 lg:data-[side=right]:data-[size=25%]:w-1/4 lg:data-[side=right]:data-[size=50%]:w-1/2 lg:data-[side=right]:data-[size=75%]:w-3/4 xl:data-[side=right]:data-[size=25%]:w-1/4 xl:data-[side=right]:data-[size=50%]:w-1/2 xl:data-[side=right]:data-[size=75%]:w-3/4 2xl:data-[side=right]:data-[size=25%]:w-1/4',
        className,
      )}
      {...props}
    >
      {children}
    </DrawerPrimitive.Content>
  );
}
function DrawerContent({
  className,
  ...props
}: Readonly<{
  children: React.ReactNode;
  className?: string;
}>) {
  return (
    <div className={cn('flex h-full w-full p-4', className)}>
      {props.children}
    </div>
  );
}

function DrawerHeader({
  className,
  ...props
}: Readonly<
  React.ComponentProps<'div'> & {
    onClose?: () => void;
  }
>) {
  return (
    <div
      data-slot="drawer-header"
      className={cn('flex items-center gap-2 px-4 pt-4', className)}
      {...props}
    >
      <DrawerPrimitive.Close
        data-slot="drawer-close"
        data-testid="drawer-close-button"
        className={cn(
          buttonVariants({ variant: 'plain', icon: true, size: 'small' }),
        )}
        onClick={props.onClose}
      >
        <Close className="hidden group-data-[side=bottom]/drawer:block md:block lg:block xl:block 2xl:block" />
        <ArrowLeft className="block group-data-[side=bottom]/drawer:hidden md:hidden lg:hidden xl:hidden 2xl:hidden" />
      </DrawerPrimitive.Close>
      {props.children}
    </div>
  );
}

function DrawerFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="drawer-footer"
      className={cn(
        'flex h-18 w-full items-center justify-end gap-2 p-4',
        className,
      )}
      {...props}
    />
  );
}

function DrawerTitle({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Title>) {
  return (
    <DrawerPrimitive.Title
      data-slot="drawer-title"
      className={cn(typographyVariants({ variant: 'h3' }), className)}
      {...props}
    />
  );
}

function DrawerDescription({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Description>) {
  return (
    <DrawerPrimitive.Description
      data-slot="drawer-description"
      className={cn(typographyVariants({ variant: 'bodyLong01' }), className)}
      {...props}
    />
  );
}

export {
  Drawer,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerRoot,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
  DrawerOverlay,
  DrawerPortal,
};
